<script setup lang="ts">
import { computed } from 'vue';
import { typeOwner, type Field, type ObjectRecord } from '@vp/shared-types';
import { live, ui } from '../editor/bridge';
import { typeContribution } from '../modules';
import { tm } from '../i18n';

/**
 * Quick fields of a module object type in the top bar (API 2.0, `fields[].quick`): shown while the
 * selection is objects of one module type; a change goes to all of them in one step.
 */
const props = defineProps<{ objs: ObjectRecord[] }>();

const type = computed(() => {
  const t0 = props.objs[0]?.type;
  return t0 && !t0.startsWith('core:') && props.objs.every((o) => o.type === t0) ? t0 : null;
});
const moduleId = computed(() => (type.value ? typeOwner(type.value) ?? '' : ''));
const fields = computed<Field[]>(() => {
  void ui.docRev;
  if (!type.value) return [];
  return (typeContribution(type.value)?.contribution.fields ?? []).filter((f) => f.quick && f.type !== 'panel').slice(0, 4);
});

function read(o: ObjectRecord, key: string): unknown {
  let cur: unknown = o.props;
  for (const k of key.split('.')) cur = cur && typeof cur === 'object' ? (cur as Record<string, unknown>)[k] : undefined;
  return cur;
}

/** The common value, or undefined when the selected objects differ. */
function value(f: Field): unknown {
  void ui.docRev;
  const def = 'default' in f ? f.default : undefined;
  const vals = props.objs.map((o) => read(o, f.key) ?? def);
  return vals.every((v) => v === vals[0]) ? vals[0] : undefined;
}

function write(f: Field, v: unknown): void {
  const parts = f.key.split('.');
  const root = parts[0];
  live.editor?.edit('module.props', (tx) => {
    for (const o of props.objs) {
      let next: unknown = v;
      if (parts.length > 1) {
        const base = structuredClone((o.props[root] as Record<string, unknown>) ?? {});
        let cur = base;
        for (let i = 1; i < parts.length - 1; i++) cur = (cur[parts[i]] ??= {}) as Record<string, unknown>;
        cur[parts[parts.length - 1]] = v;
        next = base;
      }
      tx.updateProps(o.id, { [root]: next });
    }
  });
}

const label = (f: Field) => tm(moduleId.value, f.label);
</script>

<template>
  <template v-if="fields.length">
    <template v-for="f in fields" :key="f.key">
      <span v-if="f.type === 'select' && f.options.length <= 4" class="seg-pills quick" role="radiogroup" :aria-label="label(f)" :data-quick="f.key">
        <button
          v-for="o in f.options"
          :key="String(o.value)"
          :class="{ active: value(f) === o.value }"
          role="radio"
          :aria-checked="value(f) === o.value"
          :disabled="ui.readOnly"
          @click="write(f, o.value)"
        >
          {{ tm(moduleId, o.label) }}
        </button>
      </span>
      <select v-else-if="f.type === 'select'" class="quick-in" :aria-label="label(f)" :title="label(f)" :data-quick="f.key" :value="value(f)" :disabled="ui.readOnly" @change="write(f, f.options.find((o) => String(o.value) === ($event.target as HTMLSelectElement).value)?.value)">
        <option v-if="value(f) === undefined" value="" disabled>—</option>
        <option v-for="o in f.options" :key="String(o.value)" :value="o.value">{{ tm(moduleId, o.label) }}</option>
      </select>
      <button v-else-if="f.type === 'toggle'" class="chip quick-toggle" :class="{ on: value(f) === true }" :aria-pressed="value(f) === true" :data-quick="f.key" :disabled="ui.readOnly" @click="write(f, value(f) !== true)">{{ label(f) }}</button>
      <label v-else-if="f.type === 'color'" class="chip quick-color" :title="label(f)" :data-quick="f.key">
        <input type="color" :aria-label="label(f)" :value="String(value(f) ?? '#000000').slice(0, 7)" :disabled="ui.readOnly" @change="write(f, ($event.target as HTMLInputElement).value)" />
      </label>
      <input v-else-if="f.type === 'number'" class="quick-in num" type="number" :aria-label="label(f)" :title="label(f)" :data-quick="f.key" :min="f.min" :max="f.max" :step="f.step ?? 'any'" :value="value(f) ?? ''" :disabled="ui.readOnly" @change="write(f, +($event.target as HTMLInputElement).value)" />
      <input v-else-if="f.type === 'date'" class="quick-in" type="date" :aria-label="label(f)" :title="label(f)" :data-quick="f.key" :value="String(value(f) ?? '')" :disabled="ui.readOnly" @change="write(f, ($event.target as HTMLInputElement).value)" />
      <input v-else-if="f.type === 'text'" class="quick-in text" :aria-label="label(f)" :placeholder="label(f)" :data-quick="f.key" :maxlength="f.maxLength" :value="String(value(f) ?? '')" :disabled="ui.readOnly" @change="write(f, ($event.target as HTMLInputElement).value)" />
    </template>
    <span class="sep"></span>
  </template>
</template>

<style scoped>
.quick button {
  height: 26px;
  padding: 0 9px;
  font-size: 12.5px;
}
.quick-in {
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--border);
  border-radius: 999px;
  background: var(--panel);
  color: var(--text);
  font: inherit;
  font-size: 12.5px;
}
.quick-in.num {
  width: 72px;
}
.quick-in.text {
  width: 130px;
}
.quick-toggle {
  font-size: 12.5px;
}
.quick-color input {
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: none;
  cursor: pointer;
}
.sep {
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: var(--border);
}
</style>
